import React, {useState} from 'react';

import {Button, IButtonProps} from '../../components/button';
import {
    Card,
    CardContent,
    Grid,
    GridColumn,
    IDataInfoProps,
    ITableCellProps,
    ITableProps,
    ITableRowProps,
    Scroll,
    Table,
    TTableCell,
} from '../../components';
import {Container} from '../../components/container';
import {Dropdown} from '../../components/dropdown';
import {Filter, IFilterProps, ITextFieldProps, IToggleProps, TextField, Toggle} from '../../components/forms';

export interface ITemplateCompanyProps {
    table: ITemplateCompanyTable;
    title: string;
    filter?: IFilterProps;
    toolbar?: ITemplateCompanyToolbar;
    search?: ITextFieldProps;
}

export interface ITemplateCompanyToolbar {
    action?: ITemplateCompanyAction;
    add?: IButtonProps;
    titleTooltip?: string | JSX.Element;
    defaultIsOpen? : boolean;
}

export interface ITemplateCompanyAction {
    options: IButtonProps[];
    trigger?: IButtonProps;
}

export interface ITemplateCompanyTable extends ITableProps {
    rows: ITemplateCompanyRow[] | IDataInfoProps;
}

export interface ITemplateCompanyRow extends ITableRowProps {
    actions?: ITemplateCompanyAction;
    activeCount?: number;
    inActiveCount?: number;
    subRows?: ITemplateCompanyRow[];
    toggle? : IToggleProps;
    clickableFunction?: () => void;
}

const TemplateCompany = (props: ITemplateCompanyProps): JSX.Element => {
    const [openActionsId, setOpenActionsId] = useState<string | number | undefined>(undefined);

    const renderToolbar = (toolbar: ITemplateCompanyToolbar): JSX.Element => (
        <Grid noWrap={['sm']} align={['middle-xs']} justify={['end-xs']}>
            {toolbar.add &&
            <GridColumn width={['xs']}>
                { toolbar.titleTooltip ?
                    <Dropdown
                        delayShow={200}
                        placement={'bottom'}
                        defaultOpen={toolbar.defaultIsOpen === true}
                        trigger={{children: <Button {...toolbar.add} color="white" iconLeft="plus"/>}}>
                        {toolbar.titleTooltip}
                    </Dropdown> : <Button {...toolbar.add} color="white" iconLeft="plus"/>
                }
            </GridColumn>
            }
            {toolbar.action &&
                <GridColumn width={['xs']}>
                    {renderActions(toolbar.action, 'toolbar-id')}
                </GridColumn>
            }
        </Grid>
    );

    const renderActions = (actions: ITemplateCompanyAction, id: string | number | undefined): JSX.Element => {
        if (actions.options.length === 1) {
            return <Button {...actions.options[0]} style="link"/>;
        }

        return (
            <Dropdown
                placement="bottom"
                trigger={{
                    children: <Button
                        text=""
                        style="link"
                        iconLeft="more"
                        hideLabel={true}
                        onClick={(event): void => {
                            if (actions.trigger?.onClick) {
                                actions.trigger.onClick(event);
                            }
                        }}
                    />,
                }}
                isOpen={openActionsId === id}
                onVisibleChange={(visible: boolean): void => {
                    if (visible) {
                        setOpenActionsId(id);
                    }
                    if (!visible && openActionsId === id) {
                        setOpenActionsId(undefined);
                    }
                }}
                triggerType="click"
            >
                <div className="text">
                    {actions.options.map((action: IButtonProps, i: number) => (
                        <div key={'history-action-' + i}>
                            <Button {...action} onClick={(event): void => {
                                if (action.onClick) {
                                    action.onClick(event);
                                }
                                setOpenActionsId(undefined);
                            }} style="link"/>
                        </div>
                    ))}
                </div>
            </Dropdown>
        );
    };

    const getCustomCellContent: (row: ITemplateCompanyRow, cell: ITableCellProps, index: number) => TTableCell = (row: ITemplateCompanyRow, cell: ITableCellProps, index: number) => {
        if (row.actions && index + 1 === row.cells.length) {
            return (
                <Grid align={['middle-xs']} gutter="small">
                    <GridColumn width={['max-xs']}>
                        {cell.content}
                    </GridColumn>
                    <GridColumn width={['min-xs']}>
                        {renderActions(row.actions, row.id)}
                    </GridColumn>
                </Grid>
            );
        } else if ((row.activeCount || row.inActiveCount) && index === 0) {
            return (
                <Grid align={['middle-xs']} gutter="small">
                    <GridColumn width={['max-xs', 'min-md']}>
                        {cell.content}
                    </GridColumn>
                    <GridColumn width={['max-xs', 'min-md']}>
                        <div
                            className="count-container"
                            onClick={row.clickableFunction}
                        >
                            {row.activeCount && (
                                <span className="count-text-green">{row.activeCount}</span>
                            )}
                            {row.inActiveCount && (
                                <span className="count-text-red">{row.inActiveCount}</span>
                            )}
                        </div>
                    </GridColumn>
                </Grid>
            );
        } else if (row.toggle && index === row.cells.length-2) {
            return (<Grid align={['middle-xs']} gutter='none'>
                <GridColumn width={['max-xs']}>
                    {row.toggle ? <Toggle {...row.toggle} /> : cell.content}
                </GridColumn>
            </Grid>
            );
        } else {
            return cell.content;
        }
    };

    const getTableRow: (row: ITemplateCompanyRow) => ITableRowProps = (row: ITemplateCompanyRow) => (
        {
            ...row,
            cells: row.cells.map((cell: ITableCellProps, i: number) => (
                {
                    ...cell,
                    content: getCustomCellContent(row, cell, i),
                }
            )),
            subRows: row.subRows ? row.subRows.map((subRow: ITemplateCompanyRow) => getTableRow(subRow)) : undefined,
        }
    );

    const tableProps: ITableProps = {
        ...props.table,
        rows: ((props.table.rows as ITableRowProps[]).length > 0) ? (props.table.rows as ITableRowProps[]).map((row: ITemplateCompanyRow) => (
            getTableRow(row)
        )) : props.table.rows,
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <div className="text">
                        <Grid noWrap={['lg']} justify={['between-xs']} align={['middle-xs']}>
                            <GridColumn width={props.toolbar ? ['xs-12', 'lg-8', 'xl-7'] : ['xs-12']}>
                                <Grid noWrap={['sm']} align={['middle-xs']} justify={['between-xs', 'start-lg']}>
                                    <GridColumn width={['xs-8', 'min-sm']}>
                                        <h2>{props.title}</h2>
                                    </GridColumn>
                                    {props.filter &&
                                        <GridColumn align={['middle-xs']} width={['max-sm']}>
                                            <Filter {...props.filter} hideLabel={true} className="company__filter" />
                                        </GridColumn>
                                    }
                                </Grid>
                            </GridColumn>
                            {props.toolbar &&
                                <GridColumn width={['xs-12', 'lg-4', 'xl-5']}>
                                    {renderToolbar(props.toolbar)}
                                </GridColumn>
                            }
                        </Grid>
                        <Grid justify={['end-xl', 'end-lg', 'end-sm']}>
                            {props.search &&
                                    <GridColumn width={['xs-12', 'sm-8', 'lg-6', 'xl-6']}>
                                        <TextField {...props.search} iconLeft="search" />
                                    </GridColumn>
                            }
                        </Grid>
                        <Scroll>
                            <Table {...tableProps} />
                        </Scroll>
                    </div>
                </CardContent>
            </Card>
        </Container>
    );
};

export default TemplateCompany;
